import { CdkDrag, CdkDragEnd, DragDropModule } from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  DOCUMENT,
  ElementRef,
  EventEmitter,
  Inject,
  inject,
  input,
  NgZone,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { CountdownManager } from '../../../../shared/components/countdown/countdown-manager.class';

const STORAGE_KEYS = {
  ORIGIN: 'slideshow-controls-origin',
  POSITION: 'slideshow-controls-position',
  VISIBLE: 'slideshow-controls-visible',
};

@Component({
  imports: [MatIconModule, DragDropModule, MatProgressBarModule, MatButtonModule, MatTooltipModule, TranslateModule],
  selector: 'app-slideshow-controller',
  standalone: true,
  styleUrls: ['./slideshow-controller.component.scss'],
  templateUrl: './slideshow-controller.component.html',
})
export class SlideshowControllerComponent implements OnInit, AfterViewInit {
  public totalSeconds = input<number>(0);
  public countdownManager = input.required<CountdownManager>();

  @Output() public manualChange = new EventEmitter<'next' | 'prev'>();
  @Output() public dragStarted = new EventEmitter<void>();
  @Output() public dragEnded = new EventEmitter<void>();

  @ViewChild('controlPanel') controlPanel!: ElementRef<HTMLElement>;
  @ViewChild(CdkDrag) dragInstance!: CdkDrag;

  public isVisible = true;
  public dragPosition = { x: 0, y: 0 };
  public originSide: 'left' | 'right' | 'center' = 'center';

  public remainingSeconds = signal<number>(0);
  public isPaused = signal<boolean>(false);
  private readonly ngZone = inject(NgZone);

  /** Story: "a lock icon should also be displayed next to the timer" when auto-lock is in effect. */

  public progressPercentage = computed(() => {
    const total = this.totalSeconds();
    const remaining = this.remainingSeconds();

    if (total <= 0) {
      return 0;
    }

    const elapsed = total - remaining;

    return (elapsed / total) * 100;
  });

  constructor(@Inject(DOCUMENT) private document: Document) {
    effect((onCleanup) => {
      const mgr = this.countdownManager();

      if (!mgr) {
        return;
      }

      const remainingSub = mgr.remainingSecondsSubject.subscribe((seconds: number) => {
        this.remainingSeconds.set(seconds);
      });

      const pauseSub = mgr.pauseSubject.subscribe((paused: boolean) => {
        this.isPaused.set(paused);
      });

      onCleanup(() => {
        remainingSub.unsubscribe();
        pauseSub.unsubscribe();
      });
    });
  }

  public ngOnInit(): void {
    this.loadControlSettings();
  }

  public ngAfterViewInit(): void {
    if (this.isVisible) {
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => this.checkBounds(), 500);
      });
    }
  }

  public togglePause(): void {
    const mgr = this.countdownManager();

    if (mgr?.pauseSubject) {
      mgr.pauseSubject.next(!this.isPaused());
    }
  }

  public onNext(): void {
    this.manualChange.emit('next');
  }

  public onPrevious(): void {
    this.manualChange.emit('prev');
  }

  public closeController(): void {
    this.isVisible = false;
    this.saveSettings();
  }

  public openController(side: 'left' | 'right' | 'center'): void {
    this.originSide = side;
    this.isVisible = true;
    this.dragPosition = { x: 0, y: 0 };
    this.saveSettings();
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => this.checkBounds(), 100);
    });
  }

  private loadControlSettings(): void {
    try {
      const savedPos = localStorage.getItem(STORAGE_KEYS.POSITION);

      if (savedPos) {
        this.dragPosition = JSON.parse(savedPos);
      }

      const savedVis = localStorage.getItem(STORAGE_KEYS.VISIBLE);

      if (savedVis) {
        this.isVisible = JSON.parse(savedVis);
      }

      const savedOrigin = localStorage.getItem(STORAGE_KEYS.ORIGIN);

      if (savedOrigin) {
        this.originSide = savedOrigin as 'left' | 'right' | 'center';
      }
    } catch {
      this.dragPosition = { x: 0, y: 0 };
    }
  }

  public onDragStarted(): void {
    this.dragStarted.emit();
  }

  public onDragEnded(event: CdkDragEnd): void {
    this.dragPosition = event.source.getFreeDragPosition();
    this.saveSettings();
    this.dragEnded.emit();
  }

  private checkBounds(): void {
    if (!this.controlPanel || !this.dragInstance) return;

    const rect: DOMRect = this.controlPanel.nativeElement.getBoundingClientRect();
    const container = this.document.querySelector('.slideshow-container');

    if (!container) {
      return;
    }

    const containerRect: DOMRect = container.getBoundingClientRect();

    let newX = this.dragPosition.x;
    let newY = this.dragPosition.y;

    if (rect.right > containerRect.right) {
      newX -= rect.right - containerRect.right + 20;
    }

    if (rect.bottom > containerRect.bottom) {
      newY -= rect.bottom - containerRect.bottom + 20;
    }

    if (rect.left < containerRect.left) {
      newX += containerRect.left - rect.left + 20;
    }

    if (rect.top < containerRect.top) {
      newY += containerRect.top - rect.top + 20;
    }

    if (newX !== this.dragPosition.x || newY !== this.dragPosition.y) {
      this.ngZone.run(() => {
        this.dragPosition = { x: newX, y: newY };
        this.saveSettings();
      });
    }
  }

  private saveSettings(): void {
    localStorage.setItem(STORAGE_KEYS.POSITION, JSON.stringify(this.dragPosition));
    localStorage.setItem(STORAGE_KEYS.VISIBLE, JSON.stringify(this.isVisible));
    localStorage.setItem(STORAGE_KEYS.ORIGIN, this.originSide);
  }
}
